import { parsePhoneNumberWithError } from 'libphonenumber-js';
import * as yup from 'yup';

import { Translatable } from '@/hooks/useTranslator';
import { Customer } from '@/types/customer';

export enum FieldType {
  text = 'text',
  email = 'email',
  phoneNumber = 'phoneNumber',
  paragraph = 'paragraph',
  numeric = 'numeric',
  select = 'select',
  multipleValues = 'multipleValues',
  boolean = 'boolean',
  date = 'date',
  file = 'file',
  composite = 'composite',
  embedded = 'embedded',
  list = 'list',
  cnpj = 'cnpj',
}

export interface MarkdownParagraph {
  readonly type: 'p';
  readonly content: string;
}

export interface MarkdownUnorderedList {
  readonly type: 'list';
  readonly title: string;
  readonly items: readonly string[];
}

export type MarkdownElement = MarkdownParagraph | MarkdownUnorderedList;

export type Markdown = readonly MarkdownElement[];

export const isMarkdownParagraph = (value: unknown): value is MarkdownParagraph => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const paragraph = value as Record<string, unknown>;
  return paragraph.type === 'p' && typeof paragraph.content === 'string';
};

export const isMarkdownUnorderedList = (value: unknown): value is MarkdownUnorderedList => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const list = value as Record<string, unknown>;
  return (
    list.type === 'list' &&
    typeof list.title === 'string' &&
    Array.isArray(list.items) &&
    list.items.every((item: unknown): boolean => typeof item === 'string')
  );
};

export const isMarkdownElement = (value: unknown): value is MarkdownElement => {
  return isMarkdownParagraph(value) || isMarkdownUnorderedList(value);
};

export const isMarkdown = (value: unknown): value is Markdown => {
  return Array.isArray(value) && value.every(isMarkdownElement);
};

export interface FieldBase<T> {
  readonly name: keyof T | string;
  readonly label: string;
  readonly title?: string;
  readonly description?: string | Markdown;
  readonly help?: string;
  readonly type: FieldType;
  readonly columnSpan?: number;
  readonly required: boolean;
  readonly hidden?: boolean | ((value: any) => boolean);
  readonly displayErrors?: boolean;
}

export interface SelectItem<T extends string | number = string> {
  readonly value: T;
  readonly label: string | Translatable;
  readonly role?: 'none' | 'other';
}

export interface SelectItemWithDescription<T extends string | number = string>
  extends SelectItem<T> {
  readonly description: string | Translatable;
}

export interface FieldValidatorBase {
  readonly type: 'regular-expression' | 'length';
}

export interface RegexFieldValidator extends FieldValidatorBase {
  readonly type: 'regular-expression';
  readonly description?: string;
  readonly pattern: RegExp;
}

export interface LengthFieldValidator extends FieldValidatorBase {
  readonly type: 'length';
  readonly description?: string;
  readonly min: number;
  readonly max: number;
}

export type FieldValidator = RegexFieldValidator | LengthFieldValidator;

export interface TextField<T> extends FieldBase<T> {
  readonly type: FieldType.text | FieldType.email;
  readonly validation?: FieldValidator;
}

export interface PhoneNumberField<T> extends FieldBase<T> {
  readonly type: FieldType.phoneNumber;
}

export interface ParagraphField<T> extends FieldBase<T> {
  readonly type: FieldType.paragraph;
}

export interface ItemsResolver<T extends string | number> {
  resolve: (...dependenciesValues: any[]) => Promise<readonly SelectItem<T>[]>;
  dependencies: readonly string[];
}

export const isItemsResolver = <T extends string | number>(
  thing: unknown,
): thing is ItemsResolver<T> => {
  if (thing === null || thing === undefined) {
    return false;
  } else if (typeof thing !== 'object') {
    return false;
  }

  return (
    'resolve' in thing &&
    typeof thing.resolve === 'function' &&
    'dependencies' in thing &&
    Array.isArray(thing.dependencies) &&
    thing.dependencies.every((dependency: unknown): boolean => typeof dependency === 'string')
  );
};

export interface SelectField<T, ItemType extends string | number = string> extends FieldBase<T> {
  readonly type: FieldType.select;
  readonly source: ItemsResolver<ItemType> | readonly SelectItem<ItemType>[];
  readonly multiple?: true | undefined;
}

export interface CompositeField<T> extends FieldBase<T> {
  readonly type: FieldType.composite | FieldType.embedded;
  readonly title?: string;
  readonly fields: readonly Field<unknown>[];
  readonly columnsCount: number;
}

export interface ListField<T> extends FieldBase<T> {
  readonly type: FieldType.list;
  readonly title: string;
  readonly itemTitle?: string;
  readonly fields: readonly Field<unknown>[];
}

export interface BooleanField<T> extends Omit<FieldBase<T>, 'label'> {
  readonly type: FieldType.boolean;
  readonly title: string;
}

export interface MultipleValuesField<T> extends Omit<FieldBase<T>, 'label'> {
  readonly type: FieldType.multipleValues;
  readonly title: string;
  readonly items: readonly SelectItem[];
}

export interface NumericField<T> extends FieldBase<T> {
  readonly type: FieldType.numeric;
  readonly title?: string;
  readonly precision: number;
}

export interface FileField<T> extends FieldBase<T> {
  readonly type: FieldType.file;
  readonly title: string;
  readonly path: string;
}

export interface DateField<T> extends FieldBase<T> {
  readonly type: FieldType.date;
}

export interface CNPJField<T> extends FieldBase<T> {
  readonly type: FieldType.cnpj;
  readonly validation?: FieldValidator;
}

export type Field<T> =
  | TextField<T>
  | ParagraphField<T>
  | BooleanField<T>
  | SelectField<T>
  | CompositeField<T>
  | ListField<T>
  | MultipleValuesField<T>
  | NumericField<T>
  | PhoneNumberField<T>
  | FileField<T>
  | DateField<T>
  | CNPJField<T>;

const withValidation =
  <T>(field: TextField<T> | CNPJField<T>): ((baseSchema: yup.StringSchema) => yup.StringSchema) =>
  (baseSchema: yup.StringSchema): yup.StringSchema => {
    const { validation } = field;

    if (!validation) {
      return baseSchema;
    }

    switch (validation.type) {
      case 'regular-expression':
        return baseSchema.matches(validation.pattern, validation.description);
      case 'length':
        return baseSchema
          .test('nullable', (value: unknown): boolean => {
            if (field.required) {
              return value !== null;
            }

            return true;
          })
          .min(validation.min, validation.description)
          .max(validation.max, validation.description);
      default:
        return baseSchema;
    }
  };

const createFieldSchema = <T>(
  field: Field<T>,
):
  | yup.MixedSchema
  | yup.StringSchema
  | yup.BooleanSchema
  | yup.NumberSchema
  | yup.DateSchema
  | yup.ArraySchema<Array<string | number | undefined> | undefined, yup.AnyObject, any, any>
  | yup.ObjectSchema<any> => {
  switch (field.type) {
    case FieldType.email:
    case FieldType.cnpj:
    case FieldType.text:
      return withValidation(field)(yup.string());
    case FieldType.file:
      return yup
        .mixed()
        .test(
          'is-file',
          'The field must not be empty',
          (value: unknown): yup.ValidationError | boolean => {
            if (Array.isArray(value)) {
              return value.length > 0;
            }

            return typeof value === 'string' && value !== '';
          },
        );
    case FieldType.paragraph:
      return yup.string();
    case FieldType.select:
      return selectValidator(field);
    case FieldType.boolean:
      return yup.boolean();
    case FieldType.composite:
      return createValidationSchema(field.fields);
    case FieldType.embedded:
      return createValidationSchema(field.fields);
    case FieldType.list:
      return yup
        .array()
        .of(createValidationSchema(field.fields))
        .test('not-empty', 'List must not be empty', (value: unknown): boolean => {
          if (!field.required) {
            return true;
          }

          if (Array.isArray(value)) {
            return value.length > 0;
          }

          return false;
        });
    case FieldType.multipleValues:
      return multipleValuesValidator(field);
    case FieldType.numeric:
      return yup.number();
    case FieldType.phoneNumber:
      return yup
        .string()
        .test('invalid-format', 'Must be a valida phone number', (value: unknown): boolean => {
          if (typeof value !== 'string') {
            return false;
          }

          const phoneNumber = parsePhoneNumberWithError(value);
          return phoneNumber.isValid();
        });
    case FieldType.date:
      return yup.date();
  }
};

export function createValidationSchema<T>(
  fields: ReadonlyArray<Field<T>>,
): yup.ObjectSchema<any, yup.AnyObject, any, any> {
  const flattenEmbeddedReducer = (
    reduced: ReadonlyArray<Field<T>>,
    current: Field<T>,
  ): ReadonlyArray<Field<T>> => {
    if (current.type === FieldType.embedded) {
      return [...reduced, ...current.fields];
    } else {
      return [...reduced, current];
    }
  };

  const schemaCreatorReducer = (
    schema: Record<string, yup.Schema>,
    field: Field<T>,
  ): Record<string, yup.Schema> => {
    const fieldSchema = createFieldSchema(field);
    const label = String(field.name);
    const labeledSchema = fieldSchema.label(label);
    if (field.required) {
      return { ...schema, [field.name]: labeledSchema.required() };
    }

    return { ...schema, [field.name]: labeledSchema.nullable() };
  };

  return yup.object<yup.AnyObject, any>(
    fields
      // We need to flatten the composite fields,
      // because they are just containers
      .reduce(flattenEmbeddedReducer, [])
      // Then we create the schema for each field
      .reduce(schemaCreatorReducer, {}),
  );
}

export type ValidationSchema<T> = yup.ISchema<yup.AnyObject, T>;
export type ValueType =
  | string
  | number
  | boolean
  | object
  | readonly string[]
  | readonly number[]
  | readonly boolean[];

export const isValueType = (value: unknown): value is ValueType => {
  if (value === undefined || value === null) {
    return false;
  }

  return (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'object' ||
    Array.isArray(value) // TODO: verify that items are value type as well
  );
};

export const javaEnumToSelectItem = (enumType: Record<string, string>): readonly SelectItem[] => {
  return Object.entries(enumType)
    .filter(([, value]: [string, string]): boolean => value !== '')
    .map(
      ([key, value]: [string, string]): SelectItem => ({
        label: value,
        value: key,
      }),
    );
};

const itemsAreStringValued = (
  items: readonly SelectItem<string | number>[],
): items is readonly SelectItem[] =>
  items.every((item: SelectItem<string | number>): boolean => typeof item.value === 'string');

const itemsAreNumberValued = (
  items: readonly SelectItem<string | number>[],
): items is readonly SelectItem<number>[] =>
  items.every((item: SelectItem<string | number>): boolean => typeof item.value === 'number');

const multipleValuesValidator = <T>(
  field: MultipleValuesField<T>,
): yup.ArraySchema<Array<string | undefined> | undefined, yup.AnyObject, any, any> => {
  const { items } = field;
  const itemSchema = yup.mixed<string>().oneOf(items.map((item: SelectItem): string => item.value));

  return yup
    .array()
    .of<string | undefined>(itemSchema)
    .min(field.required ? 1 : 0);
};

const selectValidator = <T>(
  field: SelectField<T>,
):
  | yup.ArraySchema<Array<string | number | undefined> | undefined, yup.AnyObject, any, any>
  | yup.StringSchema
  | yup.NumberSchema => {
  const { source } = field;

  const itemSchema = ((): yup.StringSchema | yup.NumberSchema => {
    // NOTE: The order is correct and must be respected. i.e. number first, string second
    //       because number is a subset of string so if we check for string first, it will
    //       be true for both and then typescript will consider the second if branch as having
    //       items with type `never`. Which kind of makes sense -\_(o_o)_/-
    if (isItemsResolver(source)) {
      // TODO: find a way to validate?
      return yup.string();
    } else if (itemsAreNumberValued(source)) {
      return yup.number().oneOf(source.map((item: SelectItem<number>): number => item.value));
    } else if (itemsAreStringValued(source)) {
      return yup.string().oneOf(source.map((item: SelectItem): string => item.value));
    } else {
      throw new Error('generating yup scheme for a select item: invalid item type');
    }
  })();

  if (field.multiple) {
    return yup
      .array()
      .of<string | number | undefined>(itemSchema)
      .min(field.required ? 1 : 0);
  } else {
    return itemSchema;
  }
};

export const hiddenFieldsFilter =
  (customer: Customer | undefined): ((field: Field<unknown>) => boolean) =>
  (field: Field<unknown>): boolean => {
    if (typeof field.hidden === 'function') {
      return !field.hidden(customer);
    }

    return !field.hidden;
  };

export const flattenEmbeddedReducer = (
  reduced: ReadonlyArray<Field<unknown>>,
  current: Field<unknown>,
): ReadonlyArray<Field<unknown>> => {
  if (current.type === FieldType.embedded) {
    return [...reduced, ...current.fields];
  } else {
    return [...reduced, current];
  }
};
